import { Form, Input, InputNumber, Select, Switch, DatePicker, TimePicker } from "antd";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(customParseFormat);

const DATE_FORMAT = "YYYY-MM-DD";
const TIME_FORMAT = "HH:mm";

// Parse a stored value (string) into a dayjs instance for the antd pickers.
// Returns undefined when there's nothing valid to show.
const parsePickerValue = (value, format) => {
  if (value === undefined || value === null || value === "") {
    return undefined;
  }
  const parsed = dayjs(value, format);
  return parsed.isValid() ? parsed : undefined;
};

const FormInput = ({ id, placeholder, label, defaultValue, vertical }) => {
  return (
    <Form.Item key={id} name={id}>
      <div className={`invite-user__field ${vertical ? "invite-user__field--vertical" : ""}`}>
        <p className="invite-user__field__name">{label}</p>
        <Input
          variant="borderless"
          placeholder={placeholder}
          defaultValue={defaultValue}
          maxLength={1000}
        />
      </div>
    </Form.Item>
  );
};

const FormSelect = ({
  id,
  label,
  options,
  placeholder,
  onSelectChange,
  defaultValue,
  vertical,
  hideDefaultValue,
  disabled
}) => {
  return (
    <Form.Item key={id} name={id}>
      <div className={`invite-user__field ${vertical ? "invite-user__field--vertical" : ""}`}>
        <p className="invite-user__field__name">{label}</p>
        <Select
          variant="borderless"
          disabled={disabled}
          options={options}
          defaultValue={
            hideDefaultValue
              ? undefined
              : defaultValue || options.find((o) => o.default)
          }
          placeholder={placeholder}
          onChange={(value) =>
            onSelectChange(id, options.filter((o) => o.value === value)[0].id || options.filter((o) => o.value === value)[0].value)
          }
        />
      </div>
    </Form.Item>
  );
};

const FormBoolean = ({ id, label, defaultValue, onSelectChange, vertical }) => {
  return (
    <Form.Item key={id} name={id} valuePropName="checked">
      <div className={`invite-user__field ${vertical ? "invite-user__field--vertical" : ""}`}>
        <p className="invite-user__field__name">{label}</p>
        <div className="lf-form-switch">
          <Switch
            defaultChecked={defaultValue}
            onChange={(checked) => onSelectChange(id, checked)}
            className=""
            data-testid={`invite-extra-${id}`}
          />
        </div>
      </div>
    </Form.Item>
  );
};

const FormPicker = ({ id, label, placeholder, defaultValue, onSelectChange, vertical }) => {
  return (
    <Form.Item key={id} name={id}>
      <div className={`invite-user__field ${vertical ? "invite-user__field--vertical" : ""}`}>
        <p className="invite-user__field__name">{label}</p>
        <InputNumber
          variant="borderless"
          style={{ width: "100%" }}
          placeholder={placeholder || "Pick a value..."}
          defaultValue={defaultValue != null && defaultValue !== "" ? Number(defaultValue) : undefined}
          onChange={(value) => onSelectChange(id, value == null ? null : String(value))}
        />
      </div>
    </Form.Item>
  );
};

const FormDate = ({ id, label, placeholder, defaultValue, onSelectChange, vertical }) => {
  return (
    <Form.Item key={id} name={id}>
      <div className={`invite-user__field ${vertical ? "invite-user__field--vertical" : ""}`}>
        <p className="invite-user__field__name">{label}</p>
        <DatePicker
          variant="borderless"
          placeholder={placeholder || "Select date..."}
          format={DATE_FORMAT}
          defaultValue={parsePickerValue(defaultValue, DATE_FORMAT)}
          onChange={(_, dateString) => onSelectChange(id, dateString || null)}
        />
      </div>
    </Form.Item>
  );
};

const FormTime = ({ id, label, placeholder, defaultValue, onSelectChange, vertical }) => {
  return (
    <Form.Item key={id} name={id}>
      <div className={`invite-user__field ${vertical ? "invite-user__field--vertical" : ""}`}>
        <p className="invite-user__field__name">{label}</p>
        <TimePicker
          variant="borderless"
          placeholder={placeholder || "Select time..."}
          format={TIME_FORMAT}
          defaultValue={parsePickerValue(defaultValue, TIME_FORMAT)}
          onChange={(_, timeString) => onSelectChange(id, timeString || null)}
        />
      </div>
    </Form.Item>
  );
};

const InviteContent = ({ fields, onSelectChange, vertical }) => {
  const getElement = (field) => {
    switch (field.type) {
      case "input":
        return (
          <FormInput
            id={field.enrollmentResponseId || field.id}
            placeholder={field.placeholder}
            label={field.label}
            defaultValue={field.defaultValue}
            vertical={vertical}
          />
        );
      case "select":
        return (
          <FormSelect
            id={field.enrollmentResponseId || field.id}
            placeholder={field.placeholder}
            label={field.label}
            options={field.options}
            onSelectChange={onSelectChange}
            defaultValue={field.defaultValue}
            vertical={vertical}
            hideDefaultValue={field.hideDefaultValue}
            disabled={field.disabled}
          />
        );
      case "picker":
        return (
          <FormPicker
            id={field.enrollmentResponseId || field.id}
            label={field.label}
            placeholder={field.placeholder}
            defaultValue={field.defaultValue}
            onSelectChange={onSelectChange}
            vertical={vertical}
          />
        );
      case "date":
        return (
          <FormDate
            id={field.enrollmentResponseId || field.id}
            label={field.label}
            placeholder={field.placeholder}
            defaultValue={field.defaultValue}
            onSelectChange={onSelectChange}
            vertical={vertical}
          />
        );
      case "time":
        return (
          <FormTime
            id={field.enrollmentResponseId || field.id}
            label={field.label}
            placeholder={field.placeholder}
            defaultValue={field.defaultValue}
            onSelectChange={onSelectChange}
            vertical={vertical}
          />
        );
      case "boolean":
        return (
          <FormBoolean
            id={field.enrollmentResponseId || field.id}
            label={field.label}
            defaultValue={field.defaultValue}
            onSelectChange={onSelectChange}
            vertical={vertical}
          />
        );
      default:
        return null;
    }
  };

  return fields.map((field) => getElement(field));
};

export default InviteContent;
